import React, { useState } from "react";
import { useTheme } from "@/context/ThemeContext";

/**
 * Day / Night watch-dial theme toggle.
 * A rose-gold cased dial in deep navy aventurine with gold dagger indices and a
 * horizon arc. Pressing it "sets" the radiant sun (LIGHT) behind the hill while a
 * cratered moon + stars rise (DARK). Hovering/focusing PEEKS the opposite state so
 * users can preview what they'll switch to. Animates transform/opacity only.
 */
export default function MoonphaseThemeToggle({ className = "", size = "h-14" }) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  const [peek, setPeek] = useState(false);
  // While hovering/focusing, preview the opposite state.
  const showDark = peek ? !isDark : isDark;

  const C = 50;                       // center
  const bodyCX = 50, bodyCY = 52.5;   // sun/moon center (apex of the arc)

  // 12 tapered dagger hour indices.
  const indices = Array.from({ length: 12 }, (_, i) => i * 30);

  // Fixed star speckles scattered across the sky (deterministic).
  const speckles = [
    [24, 26, 0.5], [33, 20, 0.4], [42, 30, 0.35], [61, 22, 0.45], [70, 28, 0.5],
    [76, 38, 0.35], [28, 40, 0.35], [22, 52, 0.4], [78, 52, 0.4], [66, 42, 0.3],
    [37, 24, 0.3], [55, 26, 0.35], [72, 46, 0.3], [30, 33, 0.3],
  ];

  const trans = "transform 620ms cubic-bezier(.34,1.08,.4,1), opacity 460ms ease";
  const sunStyle = {
    transformBox: "view-box", transformOrigin: `${bodyCX}px ${bodyCY}px`, transition: trans,
    transform: showDark ? "translate(-9px, 15px) scale(.72)" : "translate(0,0) scale(1)",
    opacity: showDark ? 0 : 1,
  };
  const moonStyle = {
    transformBox: "view-box", transformOrigin: `${bodyCX}px ${bodyCY}px`, transition: trans,
    transform: showDark ? "translate(0,0) scale(1)" : "translate(9px, 15px) scale(.72)",
    opacity: showDark ? 1 : 0,
  };
  const fade = { transition: "opacity 420ms ease" };

  return (
    <button
      type="button"
      onClick={toggleTheme}
      onMouseEnter={() => setPeek(true)}
      onMouseLeave={() => setPeek(false)}
      onFocus={() => setPeek(true)}
      onBlur={() => setPeek(false)}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      aria-pressed={isDark}
      title={isDark ? "Dark mode \u2014 press for light" : "Light mode \u2014 press for dark"}
      data-testid="theme-toggle-moonphase-button"
      className={
        "lch-moon-toggle relative inline-flex items-center justify-center rounded-full " +
        "transition-transform duration-200 hover:scale-[1.06] " +
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring-lch)] " +
        "focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)] " +
        size + " " + className
      }
    >
      <span className="sr-only" data-testid="theme-toggle-current-theme">{theme}</span>
      <svg aria-hidden="true" viewBox="0 0 100 100" className="h-full w-auto">
        <defs>
          <linearGradient id="lch-rose" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#f6dcc4" />
            <stop offset="32%" stopColor="#dca87f" />
            <stop offset="62%" stopColor="#bd8259" />
            <stop offset="100%" stopColor="#8f5a3a" />
          </linearGradient>
          <linearGradient id="lch-rose-2" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor="#f3d2b6" />
            <stop offset="50%" stopColor="#c88b60" />
            <stop offset="100%" stopColor="#9c6642" />
          </linearGradient>
          <radialGradient id="lch-navy" cx="42" cy="36" r="54" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#263660" />
            <stop offset="55%" stopColor="#182545" />
            <stop offset="100%" stopColor="#0b1024" />
          </radialGradient>
          <radialGradient id="lch-sun" cx="46%" cy="40%" r="65%">
            <stop offset="0%" stopColor="#ffe9a8" />
            <stop offset="45%" stopColor="#f7c04a" />
            <stop offset="100%" stopColor="#e08a1c" />
          </radialGradient>
          <radialGradient id="lch-moon" cx="40%" cy="35%" r="75%">
            <stop offset="0%" stopColor="#f4f5f7" />
            <stop offset="60%" stopColor="#cfd3d8" />
            <stop offset="100%" stopColor="#9aa0a8" />
          </radialGradient>
          <clipPath id="lch-dialclip"><circle cx="50" cy="50" r="38.5" /></clipPath>
        </defs>

        {/* Soft drop shadow */}
        <circle cx="50" cy="52" r="47" fill="#000" opacity="0.16" />

        {/* Rose-gold case + bezel rings */}
        <circle cx="50" cy="50" r="48" fill="url(#lch-rose)" />
        <circle cx="50" cy="50" r="43.5" fill="url(#lch-rose-2)" />
        <circle cx="50" cy="50" r="41" fill="none" stroke="#7c4e33" strokeWidth="0.5" opacity="0.6" />

        {/* Aventurine dial */}
        <circle cx="50" cy="50" r="38.5" fill="url(#lch-navy)" />

        <g clipPath="url(#lch-dialclip)">
          {/* Star speckles across the sky */}
          {speckles.map(([x, y, r], i) => (
            <circle key={i} cx={x} cy={y} r={r} fill="#e9edf7" opacity="0.55" />
          ))}

          {/* ---- Sun (LIGHT) ---- */}
          <g style={sunStyle}>
            <g>
              <g stroke="#f2b52e" strokeWidth="1.5" strokeLinecap="round">
                {indices.map((d, i) => (
                  <line key={i} x1={bodyCX} y1={bodyCY - 11.5} x2={bodyCX} y2={bodyCY - 14.8}
                    transform={`rotate(${d} ${bodyCX} ${bodyCY})`} />
                ))}
                <animateTransform attributeName="transform" type="rotate"
                  from={`0 ${bodyCX} ${bodyCY}`} to={`360 ${bodyCX} ${bodyCY}`}
                  dur="48s" repeatCount="indefinite" />
              </g>
              <circle cx={bodyCX} cy={bodyCY} r="7.6" fill="url(#lch-sun)"
                stroke="#c9781a" strokeWidth="0.4" />
            </g>
          </g>

          {/* ---- Moon + stars (DARK) ---- */}
          <g style={moonStyle}>
            <circle cx={bodyCX} cy={bodyCY} r="8" fill="url(#lch-moon)" stroke="#7f858d" strokeWidth="0.3" />
            {/* craters */}
            <circle cx={bodyCX - 2.4} cy={bodyCY - 2} r="1.5" fill="#b7bcc3" opacity="0.7" />
            <circle cx={bodyCX + 1.6} cy={bodyCY + 2.2} r="1.1" fill="#b0b6bd" opacity="0.65" />
            <circle cx={bodyCX - 0.4} cy={bodyCY + 3} r="0.8" fill="#b0b6bd" opacity="0.6" />
            <circle cx={bodyCX + 2.6} cy={bodyCY - 2.6} r="0.7" fill="#bcc1c8" opacity="0.6" />
            {/* faint waning-phase shadow on the right edge */}
            <path d={`M${bodyCX + 4.4} ${bodyCY - 6.6} A8 8 0 0 1 ${bodyCX + 4.4} ${bodyCY + 6.6} A6 8 0 0 0 ${bodyCX + 4.4} ${bodyCY - 6.6} Z`}
              fill="#0b1024" opacity="0.28" />
          </g>
        </g>

        {/* Decorative stars (dark only), above the dial */}
        <g style={{ ...fade, opacity: showDark ? 1 : 0 }}>
          <path d="M72 38 l0.7 1.9 l1.9 0.2 l-1.5 1.3 l0.5 1.9 l-1.6 -1 l-1.6 1 l0.5 -1.9 l-1.5 -1.3 l1.9 -0.2 z" fill="#eef1f8" opacity="0.9" />
          <circle cx="76.5" cy="46" r="0.7" fill="#eef1f8" />
          <circle cx="70" cy="30" r="0.6" fill="#eef1f8" />
          <circle cx="66.5" cy="49" r="0.5" fill="#eef1f8" />
          <animate attributeName="opacity" values={showDark ? "1;0.65;1" : "0;0;0"} dur="4s" repeatCount="indefinite" />
        </g>

        {/* Horizon arc (drawn over the bodies so they sit on the hill) */}
        <g clipPath="url(#lch-dialclip)">
          <path d="M15 63 Q50 51 85 63" fill="none" stroke="url(#lch-rose-2)" strokeWidth="1.1" strokeLinecap="round" opacity="0.95" />
        </g>

        {/* Hour dagger indices */}
        <g fill="url(#lch-rose-2)" stroke="#7c4e33" strokeWidth="0.15">
          {indices.map((d, i) => (
            <polygon key={i} points={`${C - 1.1},${C - 33.5} ${C + 1.1},${C - 33.5} ${C},${C - 28.5}`}
              transform={`rotate(${d} ${C} ${C})`} />
          ))}
        </g>

        {/* LIGHT / DARK label */}
        <g fontFamily="'Crimson Text', Georgia, serif" fontSize="6.4" letterSpacing="1.6"
           textAnchor="middle" fill="#e3b98c">
          <text x="50" y="76" style={{ ...fade, opacity: showDark ? 0 : 1 }}>LIGHT</text>
          <text x="50" y="76" style={{ ...fade, opacity: showDark ? 1 : 0 }}>DARK</text>
        </g>
      </svg>
    </button>
  );
}
